import {
  getAccessToken,
  getRefreshToken,
  getTenantId,
  setLocalTenantId,
} from "@/lib/storeManager";
import {
  getGluceraRefreshToken,
  saveGluceraAuthSession,
} from "@/lib/authSession";
import { isGluceraPublicAuthRoute } from "@/lib/authBootstrapGate";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

export const AUTH_SESSION_UPDATED = "auth:session-updated";
export const REFRESH_INTERVAL_MS = 2 * 60 * 1000;
export const ACCESS_EXPIRY_BUFFER_MS = 2 * 60 * 1000;

export type TokenPair = { access: string; refresh: string };

/**
 * Why the most recent refresh attempt failed.
 * - "none": last attempt succeeded (or none made yet)
 * - "invalid": server explicitly rejected the refresh token (401/400) → real logout
 * - "transient": network/server/timeout/config error → keep session, retry later
 * - "no-session": no refresh token stored → not logged in
 */
export type RefreshFailureKind = "none" | "invalid" | "transient" | "no-session";

let refreshPromise: Promise<TokenPair | null> | null = null;
let lastRefreshFailureKind: RefreshFailureKind = "none";

/** Reason the last refresh attempt failed. Read immediately after awaiting refresh. */
export function getLastRefreshFailureKind(): RefreshFailureKind {
  return lastRefreshFailureKind;
}

/** True only when the server definitively rejected the session (must log out). */
export function shouldLogoutAfterRefreshFailure(): boolean {
  return (
    lastRefreshFailureKind === "invalid" ||
    lastRefreshFailureKind === "no-session"
  );
}

export function getStoredRefreshToken(): string | null {
  return getGluceraRefreshToken() ?? getRefreshToken();
}

export function hasAuthSession(): boolean {
  return Boolean(getStoredRefreshToken());
}

export function isAccessExpiredOrSoon(
  bufferMs: number = ACCESS_EXPIRY_BUFFER_MS
): boolean {
  const access = getAccessToken();
  if (!access) return true;
  try {
    const decoded = jwtDecode<{ exp: number }>(access);
    return decoded.exp * 1000 < Date.now() + bufferMs;
  } catch {
    return true;
  }
}

function parseTokenResponse(data: unknown): TokenPair | null {
  if (!data || typeof data !== "object") return null;
  const payload = data as Record<string, unknown>;
  
  // Try multiple possible response structures
  const nested = payload.data as Record<string, unknown> | undefined;
  const tokens = payload.tokens as Record<string, unknown> | undefined;
  const nestedTokens = nested?.tokens as Record<string, unknown> | undefined;
  
  const access =
    (payload.access as string | undefined) ??
    (tokens?.access as string | undefined) ??
    (nested?.access as string | undefined) ??
    (nestedTokens?.access as string | undefined);
    
  const refresh =
    (payload.refresh as string | undefined) ??
    (tokens?.refresh as string | undefined) ??
    (nested?.refresh as string | undefined) ??
    (nestedTokens?.refresh as string | undefined);
    
  if (!access) return null;
  return {
    access,
    refresh: refresh ?? getStoredRefreshToken() ?? "",
  };
}

function dispatchSessionUpdated(tokens: TokenPair): void {
  window.dispatchEvent(
    new CustomEvent(AUTH_SESSION_UPDATED, { detail: tokens })
  );
}

/**
 * Refresh when access token is missing or near expiry; no-op when still valid.
 * Uses the shared refresh mutex — safe to call from multiple mount points.
 */
export async function ensureFreshAccessToken(
  bufferMs: number = ACCESS_EXPIRY_BUFFER_MS
): Promise<boolean> {
  if (isGluceraPublicAuthRoute()) return false;
  if (!hasAuthSession()) return false;
  if (!isAccessExpiredOrSoon(bufferMs)) return true;
  const tokens = await refreshAccessToken();
  return Boolean(tokens?.access);
}

/** Single entry point for token refresh (mutex via shared promise). */
export async function refreshAccessToken(): Promise<TokenPair | null> {
  if (refreshPromise) return refreshPromise;

  const refresh = getStoredRefreshToken();
  const tenantId = getTenantId();
  
  if (!refresh) {
    console.warn("⚠️ Token refresh failed: No refresh token available");
    lastRefreshFailureKind = "no-session";
    return null;
  }
  
  if (!tenantId) {
    // Missing tenant is a config/timing issue, not an invalid session.
    // Treat as transient so we never log the user out over it.
    console.warn("⚠️ Token refresh skipped: No tenant ID available (transient)");
    lastRefreshFailureKind = "transient";
    return null;
  }

  refreshPromise = (async () => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/auth/token-refresh/`,
        { refresh },
        {
          headers: {
            "Content-Type": "application/json",
            "X-Tenant-Id": tenantId,
            "X-User-Timezone":
              Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        }
      );

      const parsed = parseTokenResponse(response.data);
      if (!parsed?.access) {
        // Server responded OK but with an unexpected shape — do not force logout.
        console.error(
          "❌ Token refresh failed: Could not parse tokens from response",
          response.data
        );
        lastRefreshFailureKind = "transient";
        return null;
      }

      const pair: TokenPair = {
        access: parsed.access,
        refresh: parsed.refresh || refresh,
      };
      saveGluceraAuthSession(pair);
      dispatchSessionUpdated(pair);
      lastRefreshFailureKind = "none";
      return pair;
    } catch (error) {
      // Only a definitive rejection of the refresh token (401/400) means the
      // session is truly gone. Everything else (network down, 5xx, timeout,
      // CORS, offline) is transient — keep the user logged in and retry later.
      const status = axios.isAxiosError(error)
        ? error.response?.status
        : undefined;
      if (status === 401 || status === 400) {
        console.error("❌ Token refresh rejected by server (invalid session):", status);
        lastRefreshFailureKind = "invalid";
      } else {
        console.warn("⚠️ Token refresh failed transiently (keeping session):", error);
        lastRefreshFailureKind = "transient";
      }
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

const AUTH_STORAGE_KEYS = [
  "accessToken",
  "refreshToken",
  "userInfo",
  "glucera_access_token",
  "glucera_refresh_token",
  "glucera_user_info",
  "userProfile",
  "profileImage",
  "onboardingComplete",
  "glucera_onboarding_complete",
  "assessment_data",
  "onboarding_completed",
  "cached_meals",
  "user_preferences",
  "glucera_document_id",
  "preferencesData",
] as const;

/** Clear auth storage and send user to login (does not call logout API). */
export function clearSessionAndRedirectToLogin(): void {
  const preservedTenant = getTenantId();
  AUTH_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
  sessionStorage.clear();
  if (preservedTenant !== null) {
    setLocalTenantId(preservedTenant);
  }
  window.location.href = "/glucera/onboardingLoginpage";
}

/** 
 * Listen for storage events from other tabs and sync tokens.
 * When another tab updates tokens in localStorage, this detects and broadcasts via AUTH_SESSION_UPDATED.
 */
export function setupCrossTabSync(
  onUpdated?: (tokens: TokenPair) => void
): () => void {
  const onStorageChange = (event: StorageEvent) => {
    // Only react to changes that affect our auth tokens
    const relevantKeys = [
      "accessToken",
      "refreshToken",
      "glucera_access_token",
      "glucera_refresh_token",
    ];

    if (!event.key || !relevantKeys.includes(event.key)) return;

    // Give a small delay to ensure all storage updates are applied
    setTimeout(() => {
      const access = getAccessToken();
      const refresh = getStoredRefreshToken();

      if (access && refresh) {
        // Tokens were updated in another tab - sync them
        const tokens: TokenPair = { access, refresh };
        dispatchSessionUpdated(tokens);
        onUpdated?.(tokens);
      } else if (!access || !refresh) {
        // Tokens were cleared in another tab - logout this tab too
        clearSessionAndRedirectToLogin();
      }
    }, 10);
  };

  window.addEventListener("storage", onStorageChange);
  return () => window.removeEventListener("storage", onStorageChange);
}

/** Proactive refresh on an interval and when the app returns to foreground. */
export function setupProactiveTokenRefresh(
  onUpdated?: (tokens: TokenPair) => void
): () => void {
  const runRefresh = async () => {
    if (!hasAuthSession() || isGluceraPublicAuthRoute()) return;
    
    // Skip refresh if PWA is offline (save battery)
    if (!navigator.onLine) {
      console.log("📴 Skipping token refresh - PWA is offline");
      return;
    }
    
    const tokens = await refreshAccessToken();
    if (tokens) onUpdated?.(tokens);
  };

  const intervalId = window.setInterval(() => {
    void runRefresh();
  }, REFRESH_INTERVAL_MS);

  const onVisibility = () => {
    if (
      document.visibilityState === "visible" &&
      !isGluceraPublicAuthRoute() &&
      hasAuthSession() &&
      isAccessExpiredOrSoon()
    ) {
      void runRefresh();
    }
  };
  
  // PWA: When coming back online, try to refresh tokens
  const onOnline = () => {
    console.log("📱 PWA came back online - refreshing tokens");
    void runRefresh();
  };
  
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("online", onOnline);

  // Setup cross-tab sync
  const unsubscribeCrossTab = setupCrossTabSync(onUpdated);

  return () => {
    clearInterval(intervalId);
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("online", onOnline);
    unsubscribeCrossTab();
  };
}
